import React, { useEffect, useState } from 'react';
import {
  Card, Row, Col, Button, Tag, Typography, Space, Spin, Divider, Statistic
} from 'antd';
import {
  FileTextOutlined, DownloadOutlined, ReloadOutlined, EyeOutlined, BarChartOutlined, TeamOutlined, DollarOutlined, LineChartOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { notify } from 'components';
import ReportGenerationModal from './ReportGenerationModal';
import ReportHistoryTable from './ReportHistoryTable';
import useIsMobile from 'hooks/useIsMobile';
import useUrlFilters from 'hooks/useUrlFilters';

const { Title, Text, Paragraph } = Typography;

// Category filter lives in the URL so the view can be shared as a link.
const URL_FILTERS = {
  category: { type: 'string', default: 'All' },
};

const ReportsList = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reportTypes, setReportTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [generationModalVisible, setGenerationModalVisible] = useState(false);
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const selectedCategory = filters.category;
  const setSelectedCategory = (value) => setFilters({ category: value });

  useEffect(() => {
    fetchReportTypes();
    fetchStats();
  }, []);

  const fetchReportTypes = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/reports/reports/types/');
      if (response.status === 200) {
        setReportTypes(response.data);
      }
    } catch (error) {
      console.error('Error fetching report types:', error);
      notify('error', 'Failed to load report types');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/reports/reports/stats/');
      if (response.status === 200) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleGenerateReport = (report) => {
    setSelectedReport(report);
    setGenerationModalVisible(true);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Attendance': <TeamOutlined style={{ fontSize: 24 }} />,
      'Leave': <FileTextOutlined style={{ fontSize: 24 }} />,
      'Financial': <DollarOutlined style={{ fontSize: 24 }} />,
      'Sales': <LineChartOutlined style={{ fontSize: 24 }} />
    };
    return icons[category] || <BarChartOutlined style={{ fontSize: 24 }} />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Attendance': '#1890ff',
      'Leave': '#52c41a',
      'Financial': '#faad14',
      'Sales': '#eb2f96'
    };
    return colors[category] || '#666';
  };

  const categories = ['All', ...new Set(reportTypes.map(r => r.category))];

  const filteredReports = selectedCategory === 'All'
    ? reportTypes
    : reportTypes.filter(r => r.category === selectedCategory);

  const groupedReports = filteredReports.reduce((acc, report) => {
    if (!acc[report.category]) {
      acc[report.category] = [];
    }
    acc[report.category].push(report);
    return acc;
  }, {});

  return (
    <div style={{ padding: isMobile ? '5px 0' : '10px 0' }}>
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Title level={isMobile ? 3 : 2} style={{ marginBottom: 8 }}>
          <BarChartOutlined /> Reports & Analytics
        </Title>
        {!isMobile && (
          <Paragraph type="secondary">
            Generate comprehensive reports across Attendance, Leave, Financial, and Sales data
          </Paragraph>
        )}
      </div>

      {/* Statistics Cards */}
      {stats && (
        <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
          <Col xs={12} sm={12} md={6}>
            <Card size={isMobile ? 'small' : 'default'} bodyStyle={isMobile ? { padding: '12px' } : {}}>
              <Statistic
                title={isMobile ? 'Active' : 'Active Reports'}
                value={stats.total_reports}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#3f8600', fontSize: isMobile ? 20 : 24 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card size={isMobile ? 'small' : 'default'} bodyStyle={isMobile ? { padding: '12px' } : {}}>
              <Statistic
                title={isMobile ? 'Today' : 'Generated Today'}
                value={stats.reports_generated_today}
                prefix={<DownloadOutlined />}
                valueStyle={{ color: '#1890ff', fontSize: isMobile ? 20 : 24 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card size={isMobile ? 'small' : 'default'} bodyStyle={isMobile ? { padding: '12px' } : {}}>
              <Statistic
                title={isMobile ? 'Monthly' : 'This Month'}
                value={stats.reports_generated_this_month}
                prefix={<BarChartOutlined />}
                valueStyle={{ fontSize: isMobile ? 20 : 24 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card size={isMobile ? 'small' : 'default'} bodyStyle={isMobile ? { padding: '12px' } : {}}>
              <Statistic
                title="Pending"
                value={stats.pending_generations}
                prefix={<ReloadOutlined spin={stats.pending_generations > 0} />}
                valueStyle={{ color: stats.pending_generations > 0 ? '#faad14' : '#666', fontSize: isMobile ? 20 : 24 }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Category Filter */}
      <Card size={isMobile ? 'small' : 'default'} style={{ marginBottom: isMobile ? 16 : 24 }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 8 : 0, alignItems: isMobile ? 'flex-start' : 'center' }}>
          <Text strong style={{ marginRight: isMobile ? 0 : 16, marginBottom: isMobile ? 4 : 0 }}>Filter by Category:</Text>
          <Space size="small" wrap>
            {categories.map(category => (
              <Button
                key={category}
                size={isMobile ? 'small' : 'middle'}
                type={selectedCategory === category ? 'primary' : 'default'}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Button>
            ))}
          </Space>
        </div>
      </Card>

      {/* Report Categories */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: isMobile ? 30 : 50 }}>
          <Spin size={isMobile ? 'default' : 'large'} tip="Loading reports..." />
        </div>
      ) : (
        <>
          {Object.entries(groupedReports).map(([category, reports]) => (
            <div key={category} style={{ marginBottom: isMobile ? 20 : 32 }}>
              <Title level={isMobile ? 5 : 3} style={{ color: getCategoryColor(category), marginBottom: isMobile ? 12 : 16 }}>
                {getCategoryIcon(category)} {category} Reports
              </Title>

              <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
                {reports.map(report => (
                  <Col xs={24} sm={12} md={8} lg={6} key={report.report_type}>
                    <Card
                      hoverable
                      size={isMobile ? 'small' : 'default'}
                      style={{ height: '100%' }}
                      actions={[
                        <Button
                          type="primary"
                          size={isMobile ? 'small' : 'middle'}
                          icon={<EyeOutlined />}
                          onClick={() => navigate(`/reports/view/${report.report_type}`)}
                          block
                        >
                          View
                        </Button>,
                        <Button
                          size={isMobile ? 'small' : 'middle'}
                          icon={<DownloadOutlined />}
                          onClick={() => handleGenerateReport(report)}
                          block
                        >
                          Download
                        </Button>
                      ]}
                    >
                      <Card.Meta
                        avatar={
                          <div style={{
                            width: isMobile ? 40 : 50,
                            height: isMobile ? 40 : 50,
                            borderRadius: 8,
                            background: `${getCategoryColor(category)}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: getCategoryColor(category)
                          }}>
                            {getCategoryIcon(category)}
                          </div>
                        }
                        title={<span style={{ fontSize: isMobile ? 13 : 14 }}>{report.report_type_display}</span>}
                        description={
                          <div style={{ marginTop: isMobile ? 4 : 8 }}>
                            <Tag color={getCategoryColor(category)} style={{ fontSize: isMobile ? 10 : 12 }}>
                              {category}
                            </Tag>
                          </div>
                        }
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </>
      )}

      <Divider style={{ margin: isMobile ? '16px 0' : '24px 0' }} />

      {/* Report History */}
      <div style={{ marginTop: isMobile ? 16 : 32 }}>
        <Title level={isMobile ? 4 : 3}>
          <FileTextOutlined /> Report History
        </Title>
        <ReportHistoryTable onRefreshStats={fetchStats} isMobile={isMobile} urlPrefix="history_" />
      </div>

      {/* Generation Modal */}
      <ReportGenerationModal
        visible={generationModalVisible}
        report={selectedReport}
        onClose={() => {
          setGenerationModalVisible(false);
          setSelectedReport(null);
        }}
        onSuccess={() => {
          fetchStats();
          setGenerationModalVisible(false);
        }}
      />
    </div>
  );
};

export default ReportsList;
